import { z } from 'zod';

import { entityIdentifierSchema } from '~/shared/data-contract/chat/chat-entity';

import { blockModeSchema, questionnaireQuestionSchema } from './questionnaire-block';
import { richTextMentionElementSchema } from './rich-text';

export const diffValueTextSchema = z.object({
    type: z.literal('text'),
    value: z.string(),
});

export type DiffValueText = z.infer<typeof diffValueTextSchema>;

export const diffValueEntitiesSchema = z.object({
    refs: z.array(entityIdentifierSchema),
    type: z.literal('entities'),
});

export type DiffValueEntities = z.infer<typeof diffValueEntitiesSchema>;

// Archive/unarchive proposals list the affected items as labeled mentions,
// one-sided against empty text.
export const diffValueMentionsSchema = z.object({
    mentions: z.array(richTextMentionElementSchema),
    type: z.literal('mentions'),
});

export type DiffValueMentions = z.infer<typeof diffValueMentionsSchema>;

// Affected items with no add/remove semantics. Mentions rows are diffed side against side, which
// reads a role present only on the old side as archived; reviewer changes affect items without
// archiving them, so they arrive as a plain list the row title explains.
export const diffValueItemListSchema = z.object({
    listedItems: z.array(richTextMentionElementSchema),
    type: z.literal('item-list'),
});

export type DiffValueItemList = z.infer<typeof diffValueItemListSchema>;

export const skillChangeKindSchema = z.enum(['added', 'removed', 'level_changed']);

export type SkillChangeKind = z.infer<typeof skillChangeKindSchema>;

export const skillChangeRowSchema = z.object({
    kind: skillChangeKindSchema,
    newLevel: z.number().nullish(),
    oldLevel: z.number().nullish(),
    skill: entityIdentifierSchema,
});

export type SkillChangeRow = z.infer<typeof skillChangeRowSchema>;

export const diffValueSkillChangesSchema = z.object({
    rows: z.array(skillChangeRowSchema),
    type: z.literal('skill-changes'),
});

export type DiffValueSkillChanges = z.infer<typeof diffValueSkillChangesSchema>;

export const createdEntityTypeSchema = z.enum(['family', 'cluster', 'role', 'level']);

export type CreatedEntityType = z.infer<typeof createdEntityTypeSchema>;

// A profile section the agent authored from source documents; shown so the user approves
// the actual content, not just a title.
export const entityCreationSectionSchema = z.object({
    content: z.string(),
    name: z.string(),
});

export type EntityCreationSection = z.infer<typeof entityCreationSectionSchema>;

export const entityCreationRowSchema = z.object({
    description: z.string(),
    entityType: createdEntityTypeSchema,
    // Labeled and (where the entity carries its ancestors) clickable, like any other mention.
    parent: richTextMentionElementSchema,
    // Absent on proposals persisted before authored profile content existed.
    sections: z.array(entityCreationSectionSchema).optional(),
    title: z.string(),
});

export type EntityCreationRow = z.infer<typeof entityCreationRowSchema>;

// Create proposals list the new items one-sided against empty text, mirroring archive/unarchive.
export const diffValueEntityCreationsSchema = z.object({
    creations: z.array(entityCreationRowSchema),
    type: z.literal('entity-creations'),
});

export type DiffValueEntityCreations = z.infer<typeof diffValueEntityCreationsSchema>;

export const frameworkChangeKindSchema = z.enum([
    'framework_edited',
    'track_added',
    'track_removed',
    'track_edited',
    'level_added',
    'level_removed',
    'level_moved',
    'level_edited',
]);

export type FrameworkChangeKind = z.infer<typeof frameworkChangeKindSchema>;

export const frameworkChangeFieldSchema = z.enum(['name', 'description', 'progression_criteria']);

export type FrameworkChangeField = z.infer<typeof frameworkChangeFieldSchema>;

export const frameworkChangeRowSchema = z.object({
    after: z.string().nullish(),
    before: z.string().nullish(),
    clearsPay: z.boolean().nullish(),
    field: frameworkChangeFieldSchema.nullish(),
    fromLevel: z.number().int().nullish(),
    kind: frameworkChangeKindSchema,
    name: z.string(),
    toLevel: z.number().int().nullish(),
});

export type FrameworkChangeRow = z.infer<typeof frameworkChangeRowSchema>;

export const diffValueFrameworkChangesSchema = z.object({
    emptiedLevels: z.array(z.number().int()).nullish(),
    frameworkChanges: z.array(frameworkChangeRowSchema),
    frameworkId: z.string(),
    trackId: z.string().nullish(),
    type: z.literal('framework-changes'),
});

export type DiffValueFrameworkChanges = z.infer<typeof diffValueFrameworkChangesSchema>;

export const taskChangeKindSchema = z.enum(['added', 'removed', 'edited', 'moved']);

export type TaskChangeKind = z.infer<typeof taskChangeKindSchema>;

export const taskContentSchema = z.object({
    classification: z.object({ reasoning: z.string().nullish(), value: z.string() }).nullish(),
    description: z.string().nullish(),
    estimatedEffort: z
        .object({
            maximumHoursPerWeek: z.number(),
            minimumHoursPerWeek: z.number(),
            reasoning: z.string().nullish(),
        })
        .nullish(),
    requiredSkills: z.array(entityIdentifierSchema),
    title: z.string(),
    workflow: z
        .object({
            steps: z.array(
                z.object({
                    description: z.string().nullish(),
                    physicality: z.string().nullish(),
                    title: z.string(),
                }),
            ),
        })
        .nullish(),
});

export type TaskContent = z.infer<typeof taskContentSchema>;

export const taskContentFieldSchema = z.enum([
    'title',
    'description',
    'workflow',
    'classification',
    'estimatedEffort',
    'requiredSkills',
]);

export type TaskContentField = z.infer<typeof taskContentFieldSchema>;

export const taskChangeRowSchema = z.object({
    after: taskContentSchema.nullish(),
    before: taskContentSchema.nullish(),
    changedFields: z.array(z.string()),
    key: z.string(),
    kind: taskChangeKindSchema,
    newOrder: z.number().int().nullish(),
    oldOrder: z.number().int().nullish(),
    taskId: z.string().nullish(),
});

export type TaskChangeRow = z.infer<typeof taskChangeRowSchema>;

export const diffValueTaskChangesSchema = z.object({
    taskChanges: z.array(taskChangeRowSchema),
    type: z.literal('task-changes'),
});

export type DiffValueTaskChanges = z.infer<typeof diffValueTaskChangesSchema>;

export const diffValueSchema = z.discriminatedUnion('type', [
    diffValueTextSchema,
    diffValueEntitiesSchema,
    diffValueMentionsSchema,
    diffValueItemListSchema,
    diffValueSkillChangesSchema,
    diffValueEntityCreationsSchema,
    diffValueFrameworkChangesSchema,
    diffValueTaskChangesSchema,
]);

export type DiffValue = z.infer<typeof diffValueSchema>;

// Maps a diff row to the draft field it renders, so an inline edit can be sent
// back by id in the answer's edited-content action. Absent on rows that are not
// directly editable (skills, entities, item lists, creations).
export const diffEditTargetSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('title') }),
    z.object({ kind: z.literal('description') }),
    z.object({ kind: z.literal('section'), sectionId: z.string() }),
]);

export type DiffEditTarget = z.infer<typeof diffEditTargetSchema>;

// The tasks row edits per task (by row key), not as one value, so it is kept apart from the text targets.
export const diffTasksEditTargetSchema = z.object({
    kind: z.literal('tasks'),
    sectionId: z.null().optional(),
});

export type DiffTasksEditTarget = z.infer<typeof diffTasksEditTargetSchema>;

export const diffEditFieldOptionSchema = z.object({
    label: z.string(),
    value: z.string(),
});

export type DiffEditFieldOption = z.infer<typeof diffEditFieldOptionSchema>;

// A requested value the user may correct before accepting, such as a position plan's headcount
// target. Sent back by id in the edited-content action's `fields`. Kept apart from `editTarget`
// so clients that do not know it ignore it, and an unknown input type leaves the row read-only.
export const diffEditFieldSchema = z.discriminatedUnion('input', [
    z.object({
        id: z.string(),
        input: z.literal('integer'),
        max: z.number().nullish(),
        min: z.number().nullish(),
        value: z.string(),
    }),
    z.object({
        id: z.string(),
        input: z.enum(['text', 'multiline']),
        maxLength: z.number().int().positive().nullish(),
        value: z.string(),
    }),
    z.object({
        id: z.string(),
        input: z.literal('select'),
        options: z.array(diffEditFieldOptionSchema).min(1),
        value: z.string(),
    }),
    z.object({
        id: z.string(),
        input: z.literal('boolean'),
        value: z.enum(['true', 'false']),
    }),
]);

export type DiffEditField = z.infer<typeof diffEditFieldSchema>;

export const diffSchema = z.object({
    editField: diffEditFieldSchema.nullish().catch(null),
    editTarget: z.union([diffEditTargetSchema, diffTasksEditTargetSchema]).nullish(),
    isMain: z.boolean().nullish(),
    preferReplacementView: z.boolean().nullish(),
    newValue: diffValueSchema,
    oldValue: diffValueSchema,
    title: z.string(),
});

export type Diff = z.infer<typeof diffSchema>;

// Absent means 'default'. The backend marks irreversible proposals (deleting a user) destructive so
// the confirm option renders with the destructive treatment; the recommended (cancel) option stays first.
export const changeProposalSeveritySchema = z.enum(['default', 'destructive']);

export type ChangeProposalSeverity = z.infer<typeof changeProposalSeveritySchema>;

export const changeProposalBlockSchema = z.object({
    blockId: z.uuid(),
    callbackUrl: z.string().nullish(),
    // Present when a frozen compare payload is fetchable via GET /chat/change-proposals/{blockId};
    // null on archive proposals or when the snapshot write failed.
    compareUrl: z.string().nullish(),
    // Backend may send an empty diff list; renderers already gate on visible diffs.
    diffs: z.array(diffSchema),
    mode: blockModeSchema,
    question: questionnaireQuestionSchema,
    severity: changeProposalSeveritySchema.optional(),
    title: z.string(),
    type: z.literal('change-proposal'),
});

export type ChangeProposalBlock = z.infer<typeof changeProposalBlockSchema>;
